<template>
	<app-modal :is-open="isOpen" :title="`Change destination depot - ${request.contract_number}`" @close="close">
		<div class="space-y-5">
			<div class="grid grid-cols-1 gap-3 rounded border border-gray-200 bg-gray-50 p-4 md:grid-cols-3">
				<div>
					<div class="text-xs font-semibold uppercase tracking-wide text-gray-500">Shortage Request</div>
					<div class="mt-1 text-sm font-bold text-gray-900">{{ request.contract_number }}</div>
				</div>
				<div>
					<div class="text-xs font-semibold uppercase tracking-wide text-gray-500">Current Depot</div>
					<div class="mt-1 text-sm font-bold text-gray-900">{{ request.depot }}</div>
				</div>
				<div>
					<div class="text-xs font-semibold uppercase tracking-wide text-gray-500">Items</div>
					<div class="mt-1 text-sm font-bold text-gray-900">{{ request.items.length }}</div>
				</div>
			</div>

			<div v-if="blockingItems.length > 0" class="rounded border border-red-200 bg-red-50 p-4">
				<div class="flex items-start gap-2 text-sm font-semibold text-red-800">
					<font-awesome-icon icon="fas fa-exclamation-triangle" class="mt-0.5" />
					<span>Destination depot cannot be changed for this request.</span>
				</div>
				<div class="mt-3 max-h-40 overflow-y-auto divide-y divide-red-100 rounded bg-white/60">
					<div
						v-for="item in blockingItems"
						:key="item.id || item.item_code"
						class="grid grid-cols-[minmax(90px,130px),1fr] gap-3 px-3 py-2 text-xs text-red-900">
						<span class="font-mono font-semibold">{{ item.item_code }}</span>
						<span>{{ getBlockReason(item) }}</span>
					</div>
				</div>
			</div>

			<div class="grid grid-cols-1 gap-4 md:grid-cols-2">
				<div>
					<label class="block text-xs font-semibold uppercase tracking-wide text-gray-600" for="destination-depot">
						New Destination Depot
					</label>
					<select
						id="destination-depot"
						v-model="selectedDepot"
						:disabled="blockingItems.length > 0 || saving"
						class="mt-1 w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm focus:border-mabey-400 focus:outline-none focus:ring-2 focus:ring-mabey-400 disabled:bg-gray-100 disabled:text-gray-500">
						<option value="">Select depot</option>
						<option
							v-for="depot in availableDepotOptions"
							:key="depot"
							:value="depot">
							{{ depot }}
						</option>
					</select>
				</div>
				<div>
					<label class="block text-xs font-semibold uppercase tracking-wide text-gray-600" for="depot-change-reason">
						Reason
					</label>
					<textarea
						id="depot-change-reason"
						v-model="reason"
						:disabled="blockingItems.length > 0 || saving"
						rows="4"
						class="mt-1 w-full resize-y rounded border border-gray-300 bg-white px-3 py-2 text-sm focus:border-mabey-400 focus:outline-none focus:ring-2 focus:ring-mabey-400 disabled:bg-gray-100 disabled:text-gray-500"
						placeholder="Why is the destination depot changing?"></textarea>
				</div>
			</div>

			<div class="rounded border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
				<div class="font-semibold">This updates the destination depot on the shortage request only.</div>
				<div class="mt-2">
					Item allocations, statuses, stock checks, and delivery dates will not be changed.
				</div>
			</div>
		</div>

		<template #footer>
			<div class="flex items-center justify-end gap-3">
				<button
					type="button"
					class="rounded border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
					@click="close">
					Cancel
				</button>
				<button
					type="button"
					:disabled="!canConfirm"
					class="inline-flex items-center gap-2 rounded bg-mabey-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-mabey-700 disabled:cursor-not-allowed disabled:opacity-50"
					@click="confirm">
					<font-awesome-icon v-if="saving" icon="fas fa-spinner" spin />
					<font-awesome-icon v-else icon="fas fa-map-marker-alt" />
					Change Depot
				</button>
			</div>
		</template>
	</app-modal>
</template>

<script lang="ts" setup>
	import { computed, ref, watch } from 'vue'
	import AppModal from '@/components/base/AppModal.vue'
	import type { StockShortageItem, StockShortageResponse } from '@/data/schema/stock-shortage'

	const props = defineProps<{
		isOpen: boolean
		request: StockShortageResponse
		depotOptions: string[]
		saving?: boolean
	}>()

	const emit = defineEmits<{
		close: []
		confirm: [payload: { depot: string; reason: string }]
	}>()

	const selectedDepot = ref('')
	const reason = ref('')

	const blockingItems = computed(() => {
		return props.request.items.filter((item) => {
			const status = item.fulfillment_status || 'unfulfilled'
			return Boolean(
				item.tos_no ||
					status === 'pending-fulfillment' ||
					status === 'fulfilled' ||
					status === 'part-fulfilled'
			)
		})
	})

	const availableDepotOptions = computed(() => {
		return props.depotOptions.filter((depot) => depot && depot !== props.request.depot)
	})

	const canConfirm = computed(() => {
		return Boolean(
			!props.saving &&
				blockingItems.value.length === 0 &&
				selectedDepot.value &&
				selectedDepot.value !== props.request.depot &&
				reason.value.trim().length > 0
		)
	})

	const getBlockReason = (item: StockShortageItem) => {
		if (item.tos_no) return `Has TOS ${item.tos_no}`
		if (item.fulfillment_status === 'pending-fulfillment') return 'Pending fulfillment'
		if (item.fulfillment_status === 'fulfilled') return 'Fulfilled'
		if (item.fulfillment_status === 'part-fulfilled') return 'Part fulfilled'
		return 'Not editable'
	}

	const close = () => {
		if (props.saving) return
		emit('close')
	}

	const confirm = () => {
		if (!canConfirm.value) return
		emit('confirm', {
			depot: selectedDepot.value,
			reason: reason.value.trim()
		})
	}

	watch(
		() => props.isOpen,
		(isOpen) => {
			if (!isOpen) return
			selectedDepot.value = ''
			reason.value = ''
		},
		{ immediate: true }
	)
</script>
